import { useState, useEffect, useCallback, useRef } from 'react';
import { subscribeToUserStorage, userStorage } from '../lib/userStorage';

function readStoredValue(key, fallback) {
  try {
    const stored = userStorage.getItem(key);
    if (stored !== null) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn(`Failed to parse stored account key "${key}":`, e);
  }
  return fallback;
}

function saveStoredValue(key, value) {
  try {
    userStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn(`Failed to save account key "${key}":`, e);
  }
}

/**
 * Persist dashboard state to the signed-in user's local cache and cloud data.
 * Each dashboard gets its own storage key within that account.
 *
 * @param {string} dashboardId - Unique identifier for this dashboard
 * @param {string} storageKey - The data field name (e.g. 'rows', 'activeColumns')
 * @param {*} defaultValue - Fallback value when nothing is stored
 * @returns {[any, Function]} — same API as useState
 */
export function useDashboardState(dashboardId, storageKey, defaultValue) {
  const fullKey = `dashboard_${dashboardId}_${storageKey}`;
  const defaultValueRef = useRef(defaultValue);
  const shouldPersistRef = useRef(false);
  defaultValueRef.current = defaultValue;

  const [storedState, setStoredState] = useState(() => {
    return {
      key: fullKey,
      value: readStoredValue(fullKey, defaultValueRef.current),
    };
  });

  const value =
    storedState.key === fullKey
      ? storedState.value
      : readStoredValue(fullKey, defaultValueRef.current);

  const setValue = useCallback((nextValue) => {
    shouldPersistRef.current = true;
    setStoredState((prev) => {
      const currentValue =
        prev.key === fullKey
          ? prev.value
          : readStoredValue(fullKey, defaultValueRef.current);
      const resolvedValue =
        typeof nextValue === 'function' ? nextValue(currentValue) : nextValue;

      return {
        key: fullKey,
        value: resolvedValue,
      };
    });
  }, [fullKey]);

  // Reload from the new dashboard key when route params change.
  useEffect(() => {
    shouldPersistRef.current = false;
    setStoredState({
      key: fullKey,
      value: readStoredValue(fullKey, defaultValueRef.current),
    });
  }, [fullKey]);

  useEffect(() => subscribeToUserStorage(({ key }) => {
    if (key !== fullKey) return;
    shouldPersistRef.current = false;
    setStoredState({ key: fullKey, value: readStoredValue(fullKey, defaultValueRef.current) });
  }), [fullKey]);

  // Save only explicit local changes, not defaults or remote hydration.
  useEffect(() => {
    if (storedState.key !== fullKey) return;
    if (!shouldPersistRef.current) return;
    shouldPersistRef.current = false;
    saveStoredValue(fullKey, storedState.value);
  }, [fullKey, storedState]);

  return [value, setValue];
}

/**
 * Persist a global value within the signed-in account.
 * This is NOT per-dashboard — it's the list of all dashboards.
 *
 * @param {string} key - account storage key
 * @param {*} defaultValue - fallback
 * @returns {[any, Function]}
 */
export function usePersistedState(key, defaultValue) {
  const shouldPersistRef = useRef(false);
  const [value, setStoredValue] = useState(() => {
    try {
      const stored = userStorage.getItem(key);
      if (stored !== null) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn(`Failed to parse stored account key "${key}":`, e);
    }
    return defaultValue;
  });

  const setValue = useCallback((nextValue) => {
    shouldPersistRef.current = true;
    setStoredValue(nextValue);
  }, []);

  useEffect(() => {
    if (!shouldPersistRef.current) return;
    shouldPersistRef.current = false;
    try {
      userStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.warn(`Failed to save account key "${key}":`, e);
    }
  }, [key, value]);

  useEffect(() => subscribeToUserStorage((change) => {
    if (change.key !== key) return;
    shouldPersistRef.current = false;
    setStoredValue(readStoredValue(key, defaultValue));
  }), [defaultValue, key]);

  return [value, setValue];
}

/**
 * Delete all stored data for a given dashboard (when a dashboard is removed).
 */
export function clearDashboardStorage(dashboardId) {
  const prefix = `dashboard_${dashboardId}_`;
  const keysToRemove = [];
  for (let i = 0; i < userStorage.length; i++) {
    const key = userStorage.key(i);
    if (key && key.startsWith(prefix)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((k) => userStorage.removeItem(k));
}
